import React from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {
  BoxNumberText,
  BoxRowItem,
  BoxRows,
  DetailsHeading,
  DetailsWrapper,
  LinkButton,
  Notice,
  SummaryText,
  SummaryWrapper,
} from './BoxLine.style';
import {peekBoxes, updateLineBoxes} from '../../actions/basket.action';
import {
  formatKg,
  formatPounds,
  getLineBoxRows,
  getRemainingBoxNumbers,
  isBoxLine,
  isBoxPickerEnabled,
  isBoxUnavailable,
} from '../../helpers/boxes.helper';
import {
  BOX_MESSAGE_CODES,
  BOX_PICKER_COPY,
  BOX_PICKER_SOURCE,
} from '../../constants/constants';

export const getBoxSummaryText = (boxCount, kg) =>
  `${BOX_PICKER_COPY.BOX_COUNT(boxCount)} · ${formatKg(kg)}`;

// Whether a basket line should show boxes (or a box prompt) instead of a
// quantity stepper.
export const showsBoxControls = line =>
  isBoxPickerEnabled() && (isBoxLine(line) || isBoxUnavailable(line));

const hasLedgerNotice = line =>
  !!(
    line &&
    Array.isArray(line.messages) &&
    line.messages.some(
      message =>
        message && message.code === BOX_MESSAGE_CODES.LEDGER_UNAVAILABLE
    )
  );

// Product cards and the product page: "2 boxes · 49.68 kg" and a link to
// change them, in place of the stepper. Anything else renders `children`.
export const BoxLineSummary = ({itemId, alignLeft, children}) => {
  const dispatch = useDispatch();
  const line = useSelector(
    state => state.basketHashMap && state.basketHashMap[itemId]
  );
  const peeking = useSelector(
    state => !!state.loading && state.loading.peekingBoxes === itemId
  );
  if (!isBoxPickerEnabled() || !isBoxLine(line)) {
    return children || null;
  }
  const onChange = e => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (!peeking) {
      dispatch(peekBoxes(itemId, BOX_PICKER_SOURCE.CHANGE));
    }
  };
  return (
    <SummaryWrapper $alignLeft={alignLeft} data-rw="box-line-summary">
      <SummaryText>
        {getBoxSummaryText(line.boxNumbers.length, line.quantity)}
      </SummaryText>
      <LinkButton
        type="button"
        onClick={onChange}
        disabled={peeking}
        data-rw="box-line-change"
      >
        {BOX_PICKER_COPY.CHANGE_BOXES}
      </LinkButton>
    </SummaryWrapper>
  );
};

BoxLineSummary.propTypes = {
  itemId: PropTypes.string,
  alignLeft: PropTypes.bool,
  children: PropTypes.node,
};

// Basket page: each box with its weight and best-before, remove per box and
// "Add another box". A BOX_UNAVAILABLE line gets "Choose a box".
export const BoxLineDetailsContent = ({
  item,
  viewOnly,
  busy,
  onRemoveBox,
  onPeek,
}) => {
  const rows = getLineBoxRows(item);
  const unavailable = isBoxUnavailable(item);
  const ledgerNotice = hasLedgerNotice(item) && (
    <Notice data-rw="box-line-ledger">
      {BOX_PICKER_COPY.LEDGER_UNAVAILABLE}
    </Notice>
  );
  return (
    <DetailsWrapper data-rw={`box-line-${item.itemId}`}>
      {ledgerNotice}
      {rows.length > 0 && (
        <DetailsHeading>
          {getBoxSummaryText(rows.length, item.quantity)}
          {!!item.unitPrice &&
            ` @ ${formatPounds(item.unitPrice)} ${BOX_PICKER_COPY.PER_KG}`}
        </DetailsHeading>
      )}
      {rows.length > 0 && (
        <BoxRows>
          {rows.map(box => (
            <BoxRowItem
              key={box.boxNumber}
              data-rw={`box-line-row-${box.boxNumber}`}
            >
              <span>
                {box.weightKg !== undefined
                  ? formatKg(box.weightKg)
                  : box.boxNumber}
              </span>
              <BoxNumberText>
                {box.weightKg !== undefined && box.boxNumber}
              </BoxNumberText>
              {!viewOnly && (
                <LinkButton
                  type="button"
                  disabled={busy}
                  onClick={() => onRemoveBox(box.boxNumber)}
                  data-rw={`box-line-remove-${box.boxNumber}`}
                >
                  {BOX_PICKER_COPY.REMOVE}
                </LinkButton>
              )}
            </BoxRowItem>
          ))}
        </BoxRows>
      )}
      {!viewOnly && (
        <LinkButton
          type="button"
          disabled={busy}
          onClick={() =>
            onPeek(
              unavailable
                ? BOX_PICKER_SOURCE.UNAVAILABLE
                : BOX_PICKER_SOURCE.CHANGE
            )
          }
          data-rw="box-line-peek"
        >
          {unavailable && rows.length === 0
            ? BOX_PICKER_COPY.CHOOSE_A_BOX
            : BOX_PICKER_COPY.ADD_ANOTHER_BOX}
        </LinkButton>
      )}
    </DetailsWrapper>
  );
};

BoxLineDetailsContent.propTypes = {
  item: PropTypes.object.isRequired,
  viewOnly: PropTypes.bool,
  busy: PropTypes.bool,
  onRemoveBox: PropTypes.func,
  onPeek: PropTypes.func,
};

export const BoxLineDetails = ({item, viewOnly}) => {
  const dispatch = useDispatch();
  const busy = useSelector(
    state =>
      !!state.loading &&
      (!!state.loading.updatingBasket ||
        state.loading.peekingBoxes === item.itemId)
  );
  const onRemoveBox = boxNumber =>
    dispatch(
      updateLineBoxes(
        item.uuid,
        item.itemId,
        getRemainingBoxNumbers(item, boxNumber)
      )
    );
  const onPeek = source => dispatch(peekBoxes(item.itemId, source));
  return (
    <BoxLineDetailsContent
      item={item}
      viewOnly={viewOnly}
      busy={busy}
      onRemoveBox={onRemoveBox}
      onPeek={onPeek}
    />
  );
};

BoxLineDetails.propTypes = {
  item: PropTypes.object.isRequired,
  viewOnly: PropTypes.bool,
};
